import { motion } from 'framer-motion'
import {
    LayoutDashboard, BookOpen, Users, FileText, Upload, MessageSquare,
    Building2, GraduationCap, Handshake, LogOut, Newspaper, Shield,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import { useAuth } from '@/context/auth-context'

interface SubAdminSidebarProps {
    activeTab: string
    setActiveTab: (tab: string) => void
}

// Revenue & Payments removed from this list
const coreItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'courses', label: 'Courses', icon: BookOpen },
    { id: 'students', label: 'Students', icon: Users },
    { id: 'mentors', label: 'Assign Mentors', icon: Users },
    { id: 'counsellors', label: 'Manage Counsellors', icon: Users },
    // { id: 'revenue', label: 'Revenue & Payments', icon: IndianRupee }, // REMOVED for Sub Admin
    { id: 'tests', label: 'Create Tests', icon: FileText },
    { id: 'materials', label: 'Study Material', icon: Upload },
    { id: 'chats', label: 'Chats', icon: MessageSquare },
    { id: 'login-data', label: 'Login Data', icon: Shield },
]

const crmItems = [
    { id: 'abroad-colleges', label: 'Abroad Colleges', icon: GraduationCap },
    { id: 'abroad-leads', label: 'Abroad Leads', icon: Building2 },
    { id: 'coaching-tieups', label: 'Coaching Tie-ups', icon: Handshake },
    { id: 'partner-leads', label: 'Partner Leads', icon: Users },
    { id: 'news', label: 'News / Updates', icon: Newspaper },
    { id: 'blogs', label: 'Blog Posts', icon: BookOpen },
]

export function SubAdminSidebar({ activeTab, setActiveTab }: SubAdminSidebarProps) {
    const { user, logout } = useAuth()
    const navigate = useNavigate()

    const handleLogout = () => {
        logout()
        navigate('/login')
    }

    return (
        <GlassmorphicCard className="w-64 h-full p-4 flex flex-col">
            <div className="mb-4">
                <div className="flex items-center gap-3 mb-1">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-blue to-blue-600 flex items-center justify-center">
                        <span className="text-white font-bold text-lg">FF</span>
                    </div>
                    <div>
                        <h2 className="font-bold text-theme-primary text-sm">Sub Admin</h2>
                        <p className="text-xs text-theme-secondary">{user?.name || 'Sub Admin'}</p>
                    </div>
                </div>
            </div>

            <nav className="flex-1 space-y-1 overflow-y-auto custom-scrollbar">
                <p className="px-3 py-1.5 text-xs font-semibold text-theme-muted uppercase tracking-wider">Core</p>
                {coreItems.map((item) => {
                    const Icon = item.icon
                    const isActive = activeTab === item.id
                    return (
                        <motion.button
                            key={item.id}
                            onClick={() => setActiveTab(item.id)}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${isActive
                                ? 'bg-gradient-to-r from-primary-blue to-blue-600 text-white shadow-lg shadow-blue-500/25'
                                : 'text-theme-secondary hover:bg-slate-100 dark:hover:bg-slate-800'
                                }`}
                        >
                            <Icon className="h-5 w-5 shrink-0" />
                            <span className="text-sm font-medium truncate">{item.label}</span>
                        </motion.button>
                    )
                })}
                <p className="px-3 py-1.5 text-xs font-semibold text-theme-muted uppercase tracking-wider mt-4">CRM / Public</p>
                {crmItems.map((item) => {
                    const Icon = item.icon
                    const isActive = activeTab === item.id
                    return (
                        <motion.button
                            key={item.id}
                            onClick={() => setActiveTab(item.id)}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${isActive
                                ? 'bg-gradient-to-r from-primary-blue to-blue-600 text-white shadow-lg shadow-blue-500/25'
                                : 'text-theme-secondary hover:bg-slate-100 dark:hover:bg-slate-800'
                                }`}
                        >
                            <Icon className="h-5 w-5 shrink-0" />
                            <span className="text-sm font-medium truncate">{item.label}</span>
                        </motion.button>
                    )
                })}
            </nav>

            <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-theme-secondary hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-all"
                >
                    <LogOut className="h-5 w-5" />
                    <span className="text-sm font-medium">Logout</span>
                </button>
            </div>
        </GlassmorphicCard>
    )
}
